import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../contexts/AuthContext';
import { COLORS } from '../styles/theme';

interface ApprovalRequest {
  id: string;
  entityType: string;
  status: string;
  requestedById: string;
  reviewedById: string | null;
  payload: Record<string, unknown>;
  reviewNotes: string | null;
  createdAt: string;
  reviewedAt: string | null;
  requesterName: string | null;
  requesterEmail: string | null;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const STATUS_COLORS: Record<string, string> = {
  pending: '#b8860b',
  approved: '#2e7d32',
  rejected: '#b71c1c',
};

const ENTITY_COLORS: Record<string, string> = {
  event: '#1565c0',
  group: '#6a1b9a',
};

export function ApprovalRequestsPage() {
  const [requests, setRequests] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({});
  const [actioning, setActioning] = useState<string | null>(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const json = await apiFetch<ApprovalRequest[]>('/api/admin/approval-requests');
      if (!json.success) { setError(json.error ?? 'Failed to load requests'); return; }
      setRequests(json.data ?? []);
    } catch {
      setError('Failed to load approval requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = async (req: ApprovalRequest) => {
    setActioning(req.id);
    setActionError('');
    try {
      const json = await apiFetch(`/api/admin/approval-requests/${req.id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ review_notes: reviewNotes[req.id] || undefined }),
      });
      if (!json.success) { setActionError(json.error ?? 'Failed to approve'); return; }
      await loadRequests();
    } catch {
      setActionError('Failed to approve request');
    } finally {
      setActioning(null);
    }
  };

  const handleReject = async (req: ApprovalRequest) => {
    setActioning(req.id);
    setActionError('');
    try {
      const json = await apiFetch(`/api/admin/approval-requests/${req.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ review_notes: reviewNotes[req.id] || undefined }),
      });
      if (!json.success) { setActionError(json.error ?? 'Failed to reject'); return; }
      await loadRequests();
    } catch {
      setActionError('Failed to reject request');
    } finally {
      setActioning(null);
    }
  };

  const pending = requests.filter((r) => r.status === 'pending');
  const reviewed = requests.filter((r) => r.status !== 'pending');

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Approval Requests</h1>
          <p style={styles.subtitle}>Review steward requests for groups and events.</p>
        </div>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}
      {actionError && <div style={styles.errorBanner}>{actionError}</div>}

      {loading ? (
        <div style={styles.muted}>Loading requests…</div>
      ) : (
        <>
          <h2 style={styles.sectionTitle}>Pending ({pending.length})</h2>
          {pending.length === 0 ? (
            <div style={styles.muted}>No pending requests.</div>
          ) : (
            <div style={styles.grid}>
              {pending.map((req) => (
                <RequestCard
                  key={req.id}
                  req={req}
                  reviewNote={reviewNotes[req.id] ?? ''}
                  onNoteChange={(v) => setReviewNotes({ ...reviewNotes, [req.id]: v })}
                  onApprove={() => handleApprove(req)}
                  onReject={() => handleReject(req)}
                  actioning={actioning === req.id}
                />
              ))}
            </div>
          )}

          {reviewed.length > 0 && (
            <>
              <h2 style={{ ...styles.sectionTitle, marginTop: 32 }}>Reviewed ({reviewed.length})</h2>
              <div style={styles.grid}>
                {reviewed.map((req) => (
                  <RequestCard key={req.id} req={req} readonly />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function RequestCard({
  req,
  reviewNote = '',
  onNoteChange,
  onApprove,
  onReject,
  actioning = false,
  readonly = false,
}: {
  req: ApprovalRequest;
  reviewNote?: string;
  onNoteChange?: (v: string) => void;
  onApprove?: () => void;
  onReject?: () => void;
  actioning?: boolean;
  readonly?: boolean;
}) {
  const payload = req.payload ?? {};

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' as const }}>
          <span style={{ ...styles.badge, backgroundColor: ENTITY_COLORS[req.entityType] ?? '#555' }}>
            {req.entityType}
          </span>
          <span style={{ ...styles.badge, backgroundColor: STATUS_COLORS[req.status] ?? '#555' }}>
            {req.status}
          </span>
        </div>
        <div style={styles.cardDate}>{formatDate(req.createdAt)}</div>
      </div>

      <div style={styles.requester}>
        <strong style={{ color: COLORS.textPrimary }}>{req.requesterName ?? 'Unknown'}</strong>
        <span style={{ color: COLORS.textMuted }}> · {req.requesterEmail}</span>
      </div>

      <div style={styles.payload}>
        {Object.entries(payload).map(([k, v]) => (
          <div key={k} style={styles.payloadRow}>
            <span style={styles.payloadKey}>{k}:</span>
            <span style={styles.payloadVal}>{String(v)}</span>
          </div>
        ))}
      </div>

      {req.reviewNotes && (
        <div style={styles.reviewNotes}>
          <strong style={{ color: COLORS.textMuted, fontSize: 11 }}>Review notes:</strong> {req.reviewNotes}
        </div>
      )}

      {!readonly && (
        <div style={styles.cardActions}>
          <input
            style={styles.notesInput}
            placeholder="Review notes (optional)"
            value={reviewNote}
            onChange={(e) => onNoteChange?.(e.target.value)}
          />
          <button style={styles.btnApprove} onClick={onApprove} disabled={actioning}>
            {actioning ? '…' : 'Approve'}
          </button>
          <button style={styles.btnReject} onClick={onReject} disabled={actioning}>
            {actioning ? '…' : 'Reject'}
          </button>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1000, margin: '0 auto' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 },
  title: { color: COLORS.textPrimary, fontSize: 28, fontWeight: 700, margin: '0 0 4px' },
  subtitle: { color: COLORS.textMuted, fontSize: 14, margin: 0 },
  sectionTitle: { color: COLORS.textSecondary, fontSize: 16, fontWeight: 600, margin: '0 0 12px' },
  errorBanner: { backgroundColor: '#5c1a1a', color: '#ff8080', border: '1px solid #8b1a1a', borderRadius: 8, padding: '10px 16px', marginBottom: 20 },
  muted: { color: COLORS.textMuted, fontSize: 14, padding: 16 },
  grid: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: { backgroundColor: COLORS.backgroundCard, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 10, padding: 20 },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 },
  cardDate: { color: COLORS.textMuted, fontSize: 12 },
  requester: { fontSize: 14, marginBottom: 12 },
  badge: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, color: '#fff', borderRadius: 4, padding: '3px 8px' },
  payload: { backgroundColor: COLORS.backgroundSecondary, borderRadius: 6, padding: '8px 12px', marginBottom: 12 },
  payloadRow: { display: 'flex', gap: 8, fontSize: 13, marginBottom: 2 },
  payloadKey: { color: COLORS.textMuted, fontWeight: 600, minWidth: 100 },
  payloadVal: { color: COLORS.textPrimary },
  reviewNotes: { color: COLORS.textSecondary, fontSize: 13, marginBottom: 12, fontStyle: 'italic' },
  cardActions: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' as const },
  notesInput: { flex: 1, minWidth: 160, backgroundColor: COLORS.backgroundSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, padding: '6px 10px', fontSize: 13 },
  btnApprove: { backgroundColor: '#1b4d1b', color: '#80ff80', border: 'none', borderRadius: 6, padding: '6px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnReject: { backgroundColor: '#5c1a1a', color: '#ff8080', border: 'none', borderRadius: 6, padding: '6px 16px', fontSize: 13, cursor: 'pointer' },
};
